export const guides = [
  {
    slug: "etapes-construction",
    category: "Projet",
    title: "Les étapes clés pour construire sa maison",
    excerpt: "Recherche du terrain, compromis, permis, chantier, réception : la feuille de route complète, dans le bon ordre.",
    img: "/images/etapes-construction.png",
    sections: [
      {
        heading: "1. La recherche du terrain",
        paragraphs: [
          "Tout commence par le terrain. Avant même de visiter, vérifiez la constructibilité auprès de la mairie (plan local d'urbanisme) et demandez un certificat d'urbanisme : c'est gratuit et cela sécurise votre projet.",
          "Sur place, soyez méthodique : nature du sol, pente, viabilité, orientation, vis-à-vis. Un terrain plat et déjà raccordé vous fera économiser plusieurs milliers d'euros de terrassement et de raccordements.",
        ],
      },
      {
        heading: "2. La signature du compromis et l'achat",
        paragraphs: [
          "Le compromis vous engage : insérez impérativement des clauses suspensives (obtention du prêt, obtention du permis de construire, résultat de l'étude de sol). Sans elles, un refus de prêt ou de permis pourrait vous coûter la clause de pénalité.",
          "La signature définitive se fait chez le notaire. Prévoyez les frais de notaire sur le terrain, de l'ordre de 7 à 8 % du prix d'achat.",
        ],
      },
      {
        heading: "3. Le dépôt du permis de construire",
        paragraphs: [
          "Le dossier comprend les formulaires Cerfa, le plan de situation, le plan de masse, les plans de coupe et de façades, et l'insertion paysagère. Un dossier complet et soigné évite les demandes de pièces complémentaires qui font perdre des mois.",
          "Le délai d'instruction est de deux mois pour une maison individuelle (trois dans certains cas). Pensez au rendez-vous préalable en mairie : il est gratuit et permet de corriger le projet avant le dépôt officiel.",
        ],
      },
      {
        heading: "4. Le plan de financement",
        paragraphs: [
          "Faites jouer la concurrence entre banques et courtiers : à budget égal, un taux 0,3 point plus bas représente plusieurs milliers d'euros sur la durée du prêt. Vérifiez aussi votre éligibilité au prêt à taux zéro (PTZ) pour le neuf.",
          "Construisez votre budget global avant de chercher le financement : terrain + construction + frais annexes + marge de sécurité de 10 %.",
        ],
      },
      {
        heading: "5. Le chantier : gros œuvre puis second œuvre",
        paragraphs: [
          "Le gros œuvre comprend le terrassement, les fondations (dimensionnées grâce à l'étude de sol), l'élévation des murs, la charpente et la couverture. La maison est ensuite mise hors d'eau et hors d'air avec les menuiseries extérieures.",
          "Le second œuvre couvre l'électricité, la plomberie, la ventilation, l'isolation et les cloisons. Suivez le chantier régulièrement et photographiez chaque étape : ces photos seront précieuses en cas de désaccord.",
        ],
      },
      {
        heading: "6. La réception des travaux et la remise des clés",
        paragraphs: [
          "La réception est un moment juridique important : vous visitez la maison avec le constructeur et signez le procès-verbal. Consignez par écrit toutes les réserves (finitions, malfaçons visibles) : elles devront être levées dans les délais prévus.",
          "La réception déclenche les garanties légales : parfait achèvement (1 an), biennale (2 ans) et décennale (10 ans). Faites-vous accompagner par un expert si vous n'êtes pas à l'aise.",
        ],
      },
    ],
  },
  {
    slug: "budget-construction",
    category: "Budget",
    title: "Estimer son budget de construction",
    excerpt: "Prix au m², frais cachés, capacité d'emprunt : la méthode complète pour chiffrer votre projet sans mauvaise surprise.",
    img: "/images/budget-construction.png",
    videoId: "52YWWZkEptE",
    sections: [
      {
        heading: "Le prix au m² en 2026",
        paragraphs: [
          "Pour une maison neuve, retenez un ordre de grandeur de 1 400 €/m² en entrée de gamme, 1 800 €/m² en standard et 2 500 €/m² et plus en haut de gamme, hors terrain. Une maison de 120 m² en standard représente donc environ 216 000 € de construction.",
          "Ces prix varient selon la région, la complexité du terrain et le niveau de prestation. Utilisez notre simulateur de coût au m² pour affiner avec votre surface et votre gamme.",
        ],
      },
      {
        heading: "Composer le budget global : la règle des trois enveloppes",
        paragraphs: [
          "Votre budget total se décompose en trois enveloppes : le terrain (prix + frais de notaire de 7 à 8 %), la construction (prix au m² × surface), et les annexes (raccordements, taxe d'aménagement, étude de sol, aménagements extérieurs, cuisine).",
          "L'erreur classique consiste à ne budgéter que la construction. Les annexes représentent couramment 10 à 20 % du projet total.",
        ],
      },
      {
        heading: "Les frais que tout le monde oublie",
        paragraphs: [
          "Raccordements (eau, électricité, assainissement) : de 3 000 à 15 000 € selon la distance au réseau. Étude de sol : 1 500 à 2 500 €, obligatoire pour la revente et vivement conseillée dès l'achat du terrain. Taxe d'aménagement : variable selon la commune, demandez le taux en mairie.",
          "Ajoutez le bornage du terrain, l'assurance dommages-ouvrage (obligatoire, 2 à 4 % du coût de construction) et les frais de garantie du prêt.",
        ],
      },
      {
        heading: "Votre capacité d'emprunt",
        paragraphs: [
          "Les banques limitent l'endettement à environ 35 % de vos revenus nets, assurance comprise. À partir de votre mensualité maximale, du taux et de la durée, vous obtenez votre capacité d'emprunt : c'est exactement ce que calcule notre simulateur de budget finançable.",
          "Exemple : avec 1 400 €/mois sur 20 ans à 3,5 %, vous empruntez environ 241 000 €. Ajoutez votre apport pour obtenir le budget total du projet.",
        ],
      },
      {
        heading: "L'apport personnel et la marge de sécurité",
        paragraphs: [
          "Un apport de 10 à 20 % rassure la banque et couvre les frais annexes (notaire, garantie). Sans apport, l'emprunt à 110 % reste possible mais se négocie plus difficilement.",
          "Quel que soit votre montage, conservez une marge de 10 % du budget construction pour les imprévus : il y en a toujours.",
        ],
      },
    ],
  },
  {
    slug: "choisir-terrain",
    category: "Terrain",
    title: "Comment choisir son terrain",
    excerpt: "Constructibilité, sol, pente, viabilité, orientation : les 7 vérifications à faire avant de signer quoi que ce soit.",
    img: "/images/choisir-terrain.png",
    sections: [
      {
        heading: "1. La constructibilité",
        paragraphs: [
          "Avant toute visite, consultez le plan local d'urbanisme (PLU) en mairie ou en ligne : il définit les zones constructibles, les hauteurs autorisées, l'emprise au sol et parfois les matériaux imposés.",
          "Demandez ensuite un certificat d'urbanisme opérationnel : ce document gratuit confirme officiellement que le terrain est constructible et liste les servitudes.",
        ],
      },
      {
        heading: "2. La nature du sol",
        paragraphs: [
          "Un sol argileux implique des fondations spéciales et un surcoût de 5 000 à 20 000 €. Renseignez-vous sur la nature du sous-sol auprès de la mairie, des voisins et des cartes géotechniques publiques.",
          "L'étude de sol (1 500 à 2 500 €) est le meilleur investissement du projet : réalisez-la avant l'achat, ou insérez-la comme clause suspensive du compromis.",
        ],
      },
      {
        heading: "3. La topographie",
        paragraphs: [
          "Un terrain plat est le plus économique : terrassement simple, accès faciles. Une pente peut offrir une belle vue mais coûte cher en terrassement, murs de soutènement et accès.",
          "Méfiez-vous aussi des terrains en contrebas de la route : ruissellement des eaux pluviales quasiment garanti.",
        ],
      },
      {
        heading: "4. La viabilité et les raccordements",
        paragraphs: [
          "Vérifiez la présence de l'eau, de l'électricité et de l'assainissement (collectif ou individuel) en bordure du terrain. Chaque mètre de réseau à créer se paie : comptez de 3 000 à 15 000 € selon la distance.",
          "Un lot en début de lotissement coûte souvent moins cher à raccorder qu'un terrain isolé au fond d'un chemin.",
        ],
      },
      {
        heading: "5. L'orientation et le vis-à-vis",
        paragraphs: [
          "Une façade sud offre lumière et économies de chauffage. Visitez le terrain à différentes heures (midi et fin de journée) pour évaluer l'ensoleillement réel et les ombres portées des arbres et bâtiments voisins.",
          "Repérez les vis-à-vis actuels et futurs : un champ voisin constructible peut devenir un mur devant votre salon.",
        ],
      },
      {
        heading: "6. L'environnement et les projets du quartier",
        paragraphs: [
          "Passez à des horaires variés, y compris le soir et le week-end : trafic, bruit, odeurs, activités agricoles ou industrielles proches. Questionnez les voisins, ils sont une mine d'informations.",
          "En mairie, demandez les projets d'aménagement prévus autour du terrain : future route, zone commerciale, lotissement.",
        ],
      },
      {
        heading: "7. Négocier le prix",
        paragraphs: [
          "Chaque défaut identifié (pente, sol difficile, raccordements lointains, emprise réduite) est un argument de négociation chiffré. Comparez avec les ventes récentes de la commune via la base publique des valeurs foncières (DVF).",
          "Un terrain en vente depuis longtemps se négocie plus facilement : ne vous précipitez jamais sur la première offre.",
        ],
      },
    ],
  },
  {
    slug: "ptz-pret-a-taux-zero",
    category: "Financement",
    title: "PTZ : mode d'emploi du prêt à taux zéro pour votre maison neuve",
    excerpt: "Jusqu'à 30 % de votre projet financé à 0 % : conditions, tranches de revenus, montants et démarches pour ne pas passer à côté du prêt à taux zéro.",
    img: "https://images.unsplash.com/photo-1762146828422-50a8bd416d3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxob3VzZSUyMGJsdWVwcmludHMlMjBwbGFuc3xlbnwwfHx8fDE3ODc4NDI4ODl8MA&ixlib=rb-4.1.0&q=85",
    sections: [
      {
        heading: "1. Qu'est-ce que le prêt à taux zéro ?",
        paragraphs: [
          "Le PTZ est un prêt immobilier sans aucun intérêt, accordé par l'État en complément de votre prêt principal, pour financer l'achat ou la construction de votre résidence principale. Concrètement : une partie de votre projet est empruntée gratuitement, ce qui réduit fortement le coût total de votre financement.",
          "Bonne nouvelle pour les constructeurs : depuis le 1er avril 2025 et jusqu'au 31 décembre 2027, le PTZ est ouvert à tous les logements neufs, partout en France — y compris les maisons individuelles, quel que soit le lieu de construction.",
        ],
      },
      {
        heading: "2. Les conditions pour en bénéficier",
        paragraphs: [
          "Trois conditions principales : le logement doit devenir votre résidence principale, vous devez être primo-accédant (ne pas avoir été propriétaire de votre résidence principale au cours des deux dernières années, sauf exceptions : invalidité, catastrophe naturelle, etc.), et vos revenus ne doivent pas dépasser les plafonds de la tranche 4.",
          "Les revenus pris en compte sont votre revenu fiscal de référence de l'avant-dernière année (avis d'imposition N-1), divisé par un coefficient familial selon la taille du foyer (1 pour une personne seule, 1,5 pour deux, 1,8 pour trois, 2,1 pour quatre, 2,4 au-delà).",
        ],
      },
      {
        heading: "3. Les 4 tranches de revenus",
        paragraphs: [
          "Vos revenus déterminent votre tranche (1 à 4), selon votre zone géographique (A/A bis, B1, B2 ou C). Par exemple, pour une personne seule, le plafond d'éligibilité est de 49 000 € en zone A, 34 500 € en B1, 31 500 € en B2 et 28 500 € en C. Pour une famille de quatre personnes, il monte jusqu'à 102 900 € en zone A.",
          "Plus votre tranche est basse (revenus modestes), plus le PTZ est généreux. C'est un dispositif pensé d'abord pour les foyers les plus modestes.",
        ],
      },
      {
        heading: "4. Combien pouvez-vous emprunter à 0 % ?",
        paragraphs: [
          "Le montant du PTZ se calcule en appliquant une quotité au coût total de l'opération (plafonné selon la zone et la taille du foyer). Pour une maison individuelle neuve, la quotité est de 30 % en tranche 1, 20 % en tranches 2 et 3, et 10 % en tranche 4. Pour un logement collectif neuf, elle monte à 50 %, 40 %, 40 % et 20 %.",
          "Exemple concret : une famille de quatre personnes en zone B2, avec 60 000 € de revenus et un projet de 280 000 €, obtient environ 23 000 € de PTZ pour une maison individuelle. Faites votre propre simulation avec notre calculateur ci-dessus : il tient compte du type de logement, de la zone, du foyer et de vos revenus, et alimente automatiquement nos simulateurs de mensualité et de budget.",
        ],
      },
      {
        heading: "5. Comment se rembourse le PTZ ?",
        paragraphs: [
          "Le PTZ se rembourse avec un différé : vous ne payez rien pendant les premières années (5 à 15 ans selon votre tranche), puis vous remboursez le capital sur la période suivante. Pendant le différé, seul votre prêt principal est remboursé, ce qui adoucit le début du crédit.",
          "La durée totale du PTZ va de 20 à 25 ans selon la tranche. Votre banque construit le plan de financement global : prêt principal + PTZ, avec des mensualités lissées dans la plupart des cas.",
        ],
      },
      {
        heading: "6. La démarche, pas à pas",
        paragraphs: [
          "Vous ne demandez pas le PTZ à l'État directement : c'est votre banque qui le monte, en même temps que votre prêt principal. Estimez d'abord votre droit avec notre simulateur, puis présentez le résultat à votre banque ou à votre courtier : c'est un excellent point de départ de négociation.",
          "Toutes les banques ne sont pas égales sur le PTZ : certaines le proposent spontanément, d'autres attendent que vous le demandiez. Faites jouer la concurrence, et exigez que le PTZ soit intégré au montage si vous y êtes éligible.",
        ],
      },
      {
        heading: "7. Les erreurs à éviter",
        paragraphs: [
          "Erreur n°1 : oublier de le demander. Le PTZ n'est jamais automatique — sans demande explicite lors du montage du prêt, il est perdu. Erreur n°2 : mal déclarer la composition du foyer ou la zone, ce qui fausse la tranche. Erreur n°3 : revendre ou louer le logement trop tôt — le PTZ impose d'en faire votre résidence principale, sauf exceptions prévues par la loi.",
          "Dernier piège : se fier à un simulateur générique sans tenir compte du type de logement. Maison individuelle et logement collectif n'ont pas les mêmes quotités — notre simulateur distingue les deux.",
        ],
      },
    ],
  },
];
